/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { googleLogout, useGoogleLogin } from '@react-oauth/google';
import Cookies from 'js-cookie';
import { validateFile, getFileExtension } from '../Utils/file_functions.js'
import { getToken } from '../AuthService'
import * as default_data from '../config.js'
import { loadGoogleClientId } from '../index.js'
import { FiTrash2 } from 'react-icons/fi';
import { Button } from '@mui/material';
import { FcGoogle } from 'react-icons/fc';

var exportMimeTypes = {
  'application/vnd.google-apps.document': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.google-apps.spreadsheet': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.google-apps.presentation': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.google-apps.drawing': 'image/jpeg',
  'application/vnd.google-apps.script': 'application/vnd.google-apps.script+json'
};

const FileUploadItem = React.forwardRef(({ file, deleteFile }, ref) => {
      
  React.useImperativeHandle(ref, () => ({
    submitFile,
  }));

  const customStyles = {
    dropzoneitem: {
      listStyleType: 'none',
      textAlign: 'left',
      padding: '10px',
      margin: '10px',
      border: '1px solid #ccc',
      borderRadius: '5px',
    }
  };
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [icon, setIcon] = useState(null);

  function submitFile(user, dataset) {
    setIcon(null);
    setErrorMessage('');
    setSuccessMessage('');

    var token = getToken()
    if(token === false) {
      return;
    }

    setIcon('spinner')

    try {
      if(user) {
        var fileId = file.fileId;
        var mimeType = file.mimeType;
        var filename = file.name

        var url;
        if (mimeType in exportMimeTypes) {
          // If the file is a Google Docs file, use the export endpoint
          url = 'https://www.googleapis.com/drive/v3/files/' + fileId + '/export?mimeType=' + exportMimeTypes[mimeType];
        } else {
          // Otherwise, use the download endpoint
          url = 'https://www.googleapis.com/drive/v3/files/' + fileId + '?alt=media';
        }
        fetch(url, {
          headers: new Headers({
            'Authorization': 'Bearer ' + user.access_token
          })
        })
        .then(response => {
          if (!response.ok) {
            // If not, throw an error with the status
            throw new Error('HTTP error ' + response.status);
          }
          return response.blob();
        })
        .then(blob => {
          var reader = new FileReader();
          reader.onload = function() {
            var file_obj = new File([blob], filename, {type: mimeType});

            file.size = blob.size;
  
            if(validateFile(file) === false) {
              setErrorMessage('Invalid file type or size. Please refer to our documentation for the list of supported file types and sizes.');
              setIcon('failure')
              return;
            }
  
            // Now you can use the file to send it to your API
            const formData = new FormData();
            formData.append('file', file_obj);
            formData.append('dataset', dataset);
      
            try {
              fetch(default_data.default_chat_service_url + '/train-with-file', {
                method: 'POST',
                headers: {
                  'x-access-tokens': `${token}`
                },        
                body: formData,
                timeout: 3600000 // 60 minutes in milliseconds
              })
              .then(response => {
                if (response.ok) {
                  return response.json();
                }
                throw new Error('An error occurred while getting data.');
              })
              .then(data => {
                if(data['status'] === 200) {
                  if (data['response'].includes('Sorry,')) {
                    setErrorMessage(data['response'])
                    setIcon('failure')
                  }
                  else {
                    setSuccessMessage(data['response'])
                    setIcon('success')
                  }
                }
                else {
                  if (data['response'].includes('Sorry,')) {
                    setErrorMessage(data['response'])
                    setIcon('failure')
                  }
                  else {
                    setErrorMessage('Internal error, please contact the customer support [1].')
                    setIcon('failure')
                  }
                } 
              })       
              .catch(error => {
                setErrorMessage('Internal error, please contact the customer support [2].')
                setIcon('failure')
              });  
            } catch (error) {
              setErrorMessage('Internal error, please contact the customer support [3].')
              setIcon('failure')
            }                  
          };
          reader.readAsText(blob);
        })
        .catch(error => {
          console.error(error)
          setErrorMessage('Internal error, please contact the customer support [4].')
          setIcon('failure')    
        });
      }
    } catch (error) {
      setErrorMessage('Internal error, please contact the customer support [4].')
      setIcon('failure')
      console.log(error)
    }
  } 

  return (      
    <li style={customStyles.dropzoneitem}>
    {file.name}
      <button className="chat-log-copy-btn" onClick={(e) => {
        e.stopPropagation();
        deleteFile(file.fileId);
        }}><FiTrash2 /></button>        
        { icon === null ? '' : 
        <img style={{width:'30px'}} src={icon + '.png'} alt="{icon}" /> 
        }
        {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
        {successMessage && <p style={{ color: 'green' }}>{successMessage}</p>}
    </li>
  );
});

const GooglePopup = forwardRef(({showPicker}, ref) => {
    useImperativeHandle(ref, () => ({
      submitFiles,
    }));
  
    const [ user, setUser ] = useState(null);
    const [ profile, setProfile ] = useState(null);
    const [ files, setFiles ] = useState([]);
    const [ app_id, setAppId ] = useState([]);

    useEffect(() => {
      setFileRefs((refs) =>
        Array(files.length)
          .fill()
          .map((_, i) => refs[i] || React.createRef())
      );
    }, [files]);
  
    // Add a new state variable for the picker
    const [picker, setPicker] = useState(null);

    const [fileRefs, setFileRefs] = React.useState([]);

    var loaded_static = false;
    const [loaded, setLoaded] = useState(false);

    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
  
    // Function to load the Google Picker library
    const loadPickerLibrary = () => {
      if(default_data.default_has_google_login_enabled === false) {
        return;
      }
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.src = "https://apis.google.com/js/api.js";
      script.onload = () => {
        window.gapi.load('client:picker', initializePicker);
      };
      document.body.appendChild(script);
    };

    async function initializePicker() {
      await window.gapi.client.load('https://www.googleapis.com/discovery/v1/apis/drive/v3/rest');
    }

    // Modify the createPicker function
    const createPicker = () => {
      if (window.google && window.google.picker) {
        if (!picker) {
          const view = new window.google.picker.View(window.google.picker.ViewId.DOCS);
          const newPicker = new window.google.picker.PickerBuilder()
              .enableFeature(window.google.picker.Feature.NAV_HIDDEN)
              .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
              .setOAuthToken(user.access_token)
              .setAppId(app_id)
              .addView(view)
              .setCallback(pickerCallback)
              .build();
          setPicker(newPicker);
          newPicker.setVisible(true);
        }
        else {
          picker.setVisible(true);
        }
      }
    };

    // Callback function to handle the result of the Picker
    const pickerCallback = (data) => {
      if (data.action === window.google.picker.Action.PICKED) {
        setFiles(prevFiles => {
          let newFiles = [...prevFiles];
          for(var i = 0; i < data.docs.length; i++) {
            const fileId = data.docs[i].id;
            var type = data.docs[i].mimeType;
            if(type in exportMimeTypes) {
              type = exportMimeTypes[type];
            }
            var extension = getFileExtension(type);
            if(extension === undefined) {
              extension = '';
            }
            var fullname = data.docs[i].name
            if(!fullname.endsWith(extension)) {
              fullname += extension
            }
            var file = {'fileId': fileId, 'name': fullname, 'mimeType': data.docs[i].mimeType, 'type': type}
            if(!newFiles.some((file) => file.fileId === fileId)) {
              newFiles.push(file)
            }
          }
          return newFiles;
        });
      }
    };

    function submitFiles(dataset) {
      fileRefs.forEach((ref) => ref.current && ref.current.submitFile(user, dataset));
    }
  
    const login = useGoogleLogin({
        scope: ['https://www.googleapis.com/auth/drive.file'],
        onSuccess: (codeResponse) => {
            setSuccessMessage('')
            setErrorMessage('')
            setUser(codeResponse);
            Cookies.set('google_access_token', JSON.stringify(codeResponse), { secure: true, sameSite: 'strict' }); // Set the cookie
        },
        onError: (error) => {
          setSuccessMessage('')
          setErrorMessage('Your authentication failed. Please try again or reach us to our customer support.')        
          console.log('Login Failed:', error)
        }
    });
 
    useEffect(() => {
        if(loaded || loaded_static) {
            return;
          }
      
          setLoaded(true)
          loaded_static = true;

          const storedUser = Cookies.get('google_access_token');
          if (storedUser) {
            try {  
                setUser(JSON.parse(storedUser));
            }
            catch {}
          }
          
          loadGoogleClientId().then((response) => {
            if(response === null || response === undefined) {
              return;
            }
            setAppId(response['app_id']);
          });
      
          const script = loadPickerLibrary();
          return () => {
            if(script !== undefined) {
              document.body.removeChild(script);
            }
          };
      }, []);

    useEffect(() => {
        const fetchProfile = async () => {
          if (user) {
            try {
              await fetch(`https://www.googleapis.com/oauth2/v1/userinfo?access_token=${user.access_token}`, {
                headers: {
                  Authorization: `Bearer ${user.access_token}`,
                  Accept: 'application/json'
                }
              }).then(response => {
                if (response.ok) {
                  return response.json();
                }
                throw new Error('An error occurred.');
              })
              .then(data => {
                setProfile(data);  
              });          
            } catch (err) {
              console.log(err);
            }
          }
        };

        fetchProfile();
      }, [user]);
      
    const deleteFile = (id) => {
      const updatedFiles = files.filter((file) => file.fileId !== id);
      setFiles(updatedFiles);
    };
    
    // log out function to log the user out of google and set the profile array to null
    const logOut = () => {
        setFiles([])
        setPicker(null)
        googleLogout();
        setProfile(null);
        Cookies.remove('google_access_token'); // Remove the cookie
    };

    return (
        <div>
            {profile ? (
              <div className="picker-drop">
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px'}}>
                  <span>Logged in with Google: {profile.email}</span> - <Button variant='contained' style={{ display: 'inline' }} onClick={logOut}>Logout from Google</Button>
                </div>
                {showPicker ? 
                  <div>
                    <Button variant='contained' style={{ display: 'block', margin: '20px auto' }} onClick={createPicker}>Select Files from Google</Button> 
                    {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
                    {successMessage && <p style={{ color: 'green' }}>{successMessage}</p>}
                    {files ? 
                      <ul>
                      { files.map((file, i) => (
                        <FileUploadItem key={i} ref={fileRefs[i]} deleteFile={deleteFile} file={file} />
                      ))
                      }
                      </ul>
                      :
                      ''
                    }
                    {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
                    {successMessage && <p style={{ color: 'green' }}>{successMessage}</p>}
                  </div>
                  : 
                  ''}
              </div>
                ) : (
              showPicker ? 
                  <Button
                   startIcon={<FcGoogle />}
                   variant='contained' style={{ display: 'block', margin: 'auto' }} onClick={() => login()}>Sign in with Google</Button>
                  :
                  <Button 
                  startIcon={<FcGoogle />}
                  variant='contained' fullWidth onClick={() => login()}>Sign in with Google</Button>              
            )}
        </div>
    );
});

export default GooglePopup;
